import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Button, DataTable, FullPageStatus, Input, type DataTableColumn } from "@vv/ui";
import { requestDetailPath, teamDetailPath } from "@vv/types";
import { fetchUsers, useUsersDispatch, useUsersSelector } from "@vv/users-frontend";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ALL_AUDIT_ACTIONS,
  ALL_AUDIT_ENTITY_TYPES,
  AUDIT_ACTION_LABELS,
  AUDIT_ENTITY_TYPE_LABELS,
  AuditEntityType,
  type AuditAction,
  type AuditLogEntry,
} from "../domain/audit-log.types";
import { useAuditLogDispatch, useAuditLogSelector } from "../state/hooks";
import { fetchAuditLog } from "../state/audit-log.thunks";

export function AuditLogListPage() {
  const dispatch = useAuditLogDispatch();
  const usersDispatch = useUsersDispatch();
  const navigate = useNavigate();
  const entries = useAuditLogSelector((state) => state.auditLog.entries);
  const status = useAuditLogSelector((state) => state.auditLog.status);
  const error = useAuditLogSelector((state) => state.auditLog.error);
  const users = useUsersSelector((state) => state.users.users);

  const [entityType, setEntityType] = useState<AuditEntityType | "">("");
  const [action, setAction] = useState<AuditAction | "">("");

  useEffect(() => {
    dispatch(
      fetchAuditLog({
        entityType: entityType || undefined,
        action: action || undefined,
      }),
    );
  }, [dispatch, entityType, action]);

  useEffect(() => {
    usersDispatch(fetchUsers());
  }, [usersDispatch]);

  const actorLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const user of users) {
      map.set(user.id, user.displayName ?? user.email);
    }
    return map;
  }, [users]);

  function entityLink(entry: AuditLogEntry): string | null {
    if (entry.entityType === AuditEntityType.Team) return teamDetailPath(entry.entityId);
    if (entry.entityType === AuditEntityType.ValidationRequest)
      return requestDetailPath(entry.entityId);
    return null;
  }

  const columns: DataTableColumn<AuditLogEntry>[] = [
    {
      key: "createdAt",
      header: "When",
      render: (entry) => new Date(entry.createdAt).toLocaleString(),
      sortable: true,
      sortValue: (entry) => entry.createdAt,
    },
    {
      key: "actor",
      header: "Actor",
      render: (entry) =>
        entry.actorUserId ? (actorLabelById.get(entry.actorUserId) ?? entry.actorUserId) : "System",
    },
    {
      key: "action",
      header: "Action",
      render: (entry) => (
        <Chip size="small" label={AUDIT_ACTION_LABELS[entry.action] ?? entry.action} />
      ),
    },
    {
      key: "entity",
      header: "Entity",
      render: (entry) => {
        const label = `${AUDIT_ENTITY_TYPE_LABELS[entry.entityType] ?? entry.entityType} · ${entry.entityId.slice(0, 8)}`;
        const link = entityLink(entry);
        return link ? (
          <Box
            component="span"
            sx={{ cursor: "pointer", color: "primary.main", textDecoration: "underline" }}
            onClick={() => navigate(link)}
          >
            {label}
          </Box>
        ) : (
          label
        );
      },
    },
    {
      key: "metadata",
      header: "Details",
      render: (entry) => (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
        >
          {entry.metadata ? JSON.stringify(entry.metadata) : "—"}
        </Typography>
      ),
    },
  ];

  if (status === "failed" && entries.length === 0) {
    return (
      <FullPageStatus
        variant="error"
        title="Could not load the audit log"
        description={error ?? undefined}
        action={
          <Button variant="contained" onClick={() => dispatch(fetchAuditLog())}>
            Retry
          </Button>
        }
      />
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Audit Log
        </Typography>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Input
          select
          label="Entity type"
          value={entityType}
          onChange={(event) => setEntityType(event.target.value as AuditEntityType | "")}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All entity types</MenuItem>
          {ALL_AUDIT_ENTITY_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {AUDIT_ENTITY_TYPE_LABELS[type]}
            </MenuItem>
          ))}
        </Input>
        <Input
          select
          label="Action"
          value={action}
          onChange={(event) => setAction(event.target.value as AuditAction | "")}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All actions</MenuItem>
          {ALL_AUDIT_ACTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {AUDIT_ACTION_LABELS[option]}
            </MenuItem>
          ))}
        </Input>
      </Stack>

      {status === "succeeded" && entries.length === 0 ? (
        <FullPageStatus
          variant="empty"
          title="No audit log entries"
          description="Nothing matches these filters yet."
        />
      ) : (
        <DataTable<AuditLogEntry>
          columns={columns}
          rows={entries}
          getRowId={(entry) => entry.id}
          loading={status === "loading"}
          emptyMessage="No audit log entries."
        />
      )}
    </Box>
  );
}
